/** Persists mid-journey checkpoint for MS-REC-082 interrupted recovery after refresh. */

export interface JourneyCheckpoint {
  readonly returnTo: string;
  readonly paymentId?: string;
  readonly journey?: string;
  readonly interrupted: boolean;
}

const KEY_PREFIX = 'customer:journey-checkpoint:';

function storageKey(tenantCode: string): string {
  return `${KEY_PREFIX}${tenantCode}`;
}

const RELOAD_FLAG_PREFIX = 'customer:journey-reload:';

function reloadFlagKey(tenantCode: string): string {
  return `${RELOAD_FLAG_PREFIX}${tenantCode}`;
}

/**
 * Per-tenant write suppress armed by `clearJourneyCheckpoint`.
 * Blocks beforeunload/pagehide interrupt re-writes during `location.replace` (G4).
 * Lift via `allowJourneyCheckpointWrites` when a new payment-pending scope starts.
 */
const suppressedCheckpointWrites = new Set<string>();

/**
 * One-shot for this JS realm: PerformanceNavigationTiming.type === 'reload'
 * stays true for the whole SPA session after F5. Consume on first read so a later
 * SPA pagehide + interrupted checkpoint cannot reopen /recovery/interrupted (MOB-05 / G7).
 */
let documentReloadSignalConsumed = false;

/** True when this document was loaded via reload (feature-detect Navigation Timing). */
export function wasDocumentLoadedByReload(): boolean {
  if (typeof performance === 'undefined') {
    return false;
  }
  if (typeof performance.getEntriesByType === 'function') {
    const entries = performance.getEntriesByType('navigation');
    const entry = entries[0];
    if (entry !== undefined && 'type' in entry) {
      return (entry as PerformanceNavigationTiming).type === 'reload';
    }
  }
  // Legacy PerformanceNavigation (older WebKit); TYPE_RELOAD === 1
  const legacy = (
    performance as Performance & {
      navigation?: { type?: number };
    }
  ).navigation;
  if (legacy !== undefined && typeof legacy.type === 'number') {
    return legacy.type === 1;
  }
  return false;
}

/**
 * POP-safe reload signal for MS-REC-082: returns true at most once per document load
 * when Navigation Timing (or legacy) reports type=reload.
 */
export function consumeDocumentReloadSignal(): boolean {
  if (documentReloadSignalConsumed) {
    return false;
  }
  documentReloadSignalConsumed = true;
  return wasDocumentLoadedByReload();
}

/** Test helper — resets one-shot Navigation Timing consume gate. */
export function resetDocumentReloadSignalForTests(): void {
  documentReloadSignalConsumed = false;
}

export function markJourneyReloadForRecovery(tenantCode: string): void {
  const storage = readStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.setItem(reloadFlagKey(tenantCode), '1');
  } catch {
    // sessionStorage may be unavailable
  }
}

export function consumeJourneyReloadFlag(tenantCode: string): boolean {
  const storage = readStorage();
  if (storage === null) {
    return false;
  }
  try {
    const key = reloadFlagKey(tenantCode);
    const value = storage.getItem(key);
    if (value === null) {
      return false;
    }
    storage.removeItem(key);
    return value === '1';
  } catch {
    return false;
  }
}

function readStorage(): Storage | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return null;
  }
  return window.sessionStorage;
}

/** Lift write suppress so a new payment-pending scope may persist a checkpoint. */
export function allowJourneyCheckpointWrites(tenantCode: string): void {
  suppressedCheckpointWrites.delete(tenantCode);
}

export function writeJourneyCheckpoint(
  tenantCode: string,
  checkpoint: JourneyCheckpoint,
): void {
  if (suppressedCheckpointWrites.has(tenantCode)) {
    return;
  }
  const storage = readStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.setItem(storageKey(tenantCode), JSON.stringify(checkpoint));
  } catch {
    // sessionStorage may be unavailable in private mode
  }
}

export function readJourneyCheckpoint(tenantCode: string): JourneyCheckpoint | null {
  const storage = readStorage();
  if (storage === null) {
    return null;
  }
  try {
    const raw = storage.getItem(storageKey(tenantCode));
    if (raw === null || raw.length === 0) {
      return null;
    }
    const parsed = JSON.parse(raw) as JourneyCheckpoint;
    if (
      typeof parsed.returnTo !== 'string' ||
      parsed.returnTo.length === 0 ||
      typeof parsed.interrupted !== 'boolean'
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearJourneyCheckpoint(tenantCode: string): void {
  suppressedCheckpointWrites.add(tenantCode);
  const storage = readStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.removeItem(storageKey(tenantCode));
  } catch {
    // sessionStorage may be unavailable in private mode
  }
}

export function resetJourneyCheckpointForTests(): void {
  suppressedCheckpointWrites.clear();
  const storage = readStorage();
  if (storage === null) {
    resetDocumentReloadSignalForTests();
    return;
  }
  for (const key of Object.keys(storage)) {
    if (key.startsWith(KEY_PREFIX) || key.startsWith(RELOAD_FLAG_PREFIX)) {
      storage.removeItem(key);
    }
  }
  resetDocumentReloadSignalForTests();
}
